import ErrorBoundary from "components/layout/error-boundary";
import MaintenanceModal from "components/layout/maintenance-modal";
import useHttp from "hooks/useHttp";
import useStore from "hooks/useStore";
import { useEffect, useState } from "react";
import { BrowserRouter } from "react-router-dom";
import AppRoutes from "routes/routes";
import GlobalStyles from "styles";
import { domains } from "utils/helpers/enums/shared.enums";
import ReactPixel from "react-facebook-pixel";

function App() {
  const [pixelData, setPixelsData] = useState<any>({});
  const {
    setDarkTheme,
    getDomainType,
    setLoading,
    setSiteZoomLevel,
    getToken,
    handleSetOrganizationLocally,
  } = useStore();
  const {
    configureHeaders,
    configureInterceptors,
    getCurrentOrganization,
    getMyPermissions,
    getPublicOrgSetting,
  } = useHttp();

  const updateTheme = () => {
    const queryParams = new URLSearchParams(window.location.search);
    let theme = queryParams.get("theme");
    if (theme !== "dark" && theme !== "light") {
      theme = window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
    }

    theme = "light";

    if (theme === "dark") {
      setDarkTheme(true);
    } else {
      setDarkTheme(false);
    }
    const themeColorMetaTag = document.querySelector(
      'meta[name="theme-color"]'
    );
    if (themeColorMetaTag) {
      themeColorMetaTag.setAttribute(
        "content",
        theme === "dark" ? "#000000" : "#ffffff"
      );
    }
  };

  const getScreenInfo = () => {
    const { innerWidth: width, innerHeight: height } = window;
    document.body.style.setProperty("--width", `${width}px`);
    document.body.style.setProperty("--height", `${height}px`);
    window.scrollTo(0, 0);
  };

  configureInterceptors();

  useEffect(() => {
    setLoading(false);
    configureHeaders();
    handleSetOrganizationLocally();
    getCurrentOrganizationData();
    getPermissionsOfCurrentUser();
    // getCurrentOrgSetting(setPixelsData);
    // getCurrentOrganization(setPixelsData);
    setSiteZoomLevel();
    document.body.classList.add("p-custom-scrollbar-8");
    window
      .matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", updateTheme);

    window.addEventListener("resize", getScreenInfo);
    window.addEventListener("orientationchange", getScreenInfo);
    getScreenInfo();
    updateTheme();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.target instanceof HTMLInputElement &&
        event.target.type === "number" &&
        (event.key === "ArrowUp" || event.key === "ArrowDown")
      ) {
        event.preventDefault();
      }
    };

    const handleWheel = (event: WheelEvent) => {
      if (
        event.target instanceof HTMLInputElement &&
        event.target.type === "number"
      ) {
        event.target.blur();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("wheel", handleWheel);
    };
  }, []);
  const getCurrentOrganizationData = () => {
    const domainType = getDomainType();
    if (domainType !== domains.mainDomain) {
      getPublicOrgSetting(setPixelsData);
    }
  };

  const getPermissionsOfCurrentUser = () => {
    const token = getToken();
    if (token) getMyPermissions();
  };
  useEffect(() => {
    const pixelId = pixelData?.facebook_pixel_id;
    if (pixelId && typeof pixelId === "string" && pixelId.trim() !== "") {
      ReactPixel.init(pixelId);
      ReactPixel.pageView();
    }
    
    const tiktokPixelId = pixelData?.tiktok_pixel_id;
    if (tiktokPixelId && typeof tiktokPixelId === "string" && tiktokPixelId.trim() !== "") {
      /* eslint-disable */
      (function (w: any, d: any, t: any) {
        w.TiktokAnalyticsObject = t;
        var ttq = w[t] = w[t] || [];
        ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie", "holdConsent", "revokeConsent", "grantConsent"];
        ttq.setAndDefer = function(t: any, e: any) { t[e] = function() { t.push([e].concat(Array.prototype.slice.call(arguments, 0))); }; };
        for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
        ttq.instance = function(t: any) {
          for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]);
          return e;
        };
        ttq.load = function(e: any, n?: any) {
          var r = "https://analytics.tiktok.com/i18n/pixel/events.js";
          ttq._i = ttq._i || {};
          ttq._i[e] = [];
          ttq._i[e]._u = r;
          ttq._t = ttq._t || {};
          ttq._t[e] = +new Date();
          ttq._o = ttq._o || {};
          ttq._o[e] = n || {};
          var scr = d.createElement("script");
          scr.type = "text/javascript";
          scr.async = !0;
          scr.src = r + "?sdkid=" + e + "&lib=" + t;
          var js = d.getElementsByTagName("script")[0];
          js.parentNode.insertBefore(scr, js);
        };
        ttq.load(tiktokPixelId);
        ttq.page();
      })(window, document, 'ttq');
      /* eslint-enable */
    }

    const googleTagId = pixelData?.google_tag_id;
    if (googleTagId && typeof googleTagId === "string" && googleTagId.trim() !== "") {
      const tagId = googleTagId.trim();
      /* eslint-disable */
      if (tagId.startsWith("G-")) {
        (function (w: any, d: Document, s: string, i: string) {
          w.dataLayer = w.dataLayer || [];
          w.gtag = function () {
            w.dataLayer.push(arguments);
          };
          w.gtag("js", new Date());
          w.gtag("config", i);
          const f = d.getElementsByTagName(s)[0];
          const j = d.createElement(s) as HTMLScriptElement;
          j.async = true;
          j.src = "https://www.googletagmanager.com/gtag/js?id=" + i;
          f.parentNode?.insertBefore(j, f);
        })(window, document, "script", tagId);
      } else {
        (function (w: any, d: Document, s: string, l: string, i: string) {
          w[l] = w[l] || [];
          w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
          const f = d.getElementsByTagName(s)[0];
          const j = d.createElement(s) as HTMLScriptElement;
          const dl = l !== "dataLayer" ? "&l=" + l : "";
          j.async = true;
          j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
          f.parentNode?.insertBefore(j, f);
        })(window, document, "script", "dataLayer", tagId);
      }
      /* eslint-enable */
    }
    // const handleLead = () => {
    //   ReactPixel.track("Lead");
    // };
  }, [pixelData]);

  return (
    <ErrorBoundary>
      <GlobalStyles />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
      {Number(pixelData?.is_maintenance_on) === 1 &&
        (window.location.pathname.startsWith("/private/e-portal") ||
          window.location.pathname.startsWith("/e-portal")) && (
          <MaintenanceModal
            text={
              pixelData?.maintenance_mode_text ||
              "The System Is Under Update! Please Visit After Few Minutes"
            }
          />
        )}
    </ErrorBoundary>
  );
}

export default App;
